import { CollapseProps, Flex, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FC, useMemo } from "react";
import { CheckoutOrder } from "tm-shared/types";
import { formatUAHNarrow, formatUAHStandard } from "tm-shared/utils";

import { PrimaryText } from "@/shared/ui/Typography";

import {
    StyledCollapse,
    StyledCollapseWrapper,
    StyledColumnNameText,
    StyledTableWrapper,
    StyledTotalSumText,
    StyledValueText,
} from "./Styled";

interface StatisticAccordionProps {
    checkouts: CheckoutOrder[];
}

export const StatisticAccordion: FC<StatisticAccordionProps> = ({
    checkouts,
}) => {
    const data = useMemo(() => {
        const formMap: Record<
            string,
            {
                key: string;
                name: string;
                open: number;
                closed: number;
                total: number;
            }
        > = {};

        const accumulate = (order: CheckoutOrder) => {
            const form = order.paymentForm || "Невідома";
            const isClosed = order.closed;
            const total = Object.values(order.products).reduce(
                (sum, p) => sum + p.count * p.price,
                0,
            );

            if (!formMap[form]) {
                formMap[form] = {
                    key: form,
                    name: `Форма ${form}`,
                    open: 0,
                    closed: 0,
                    total: 0,
                };
            }

            if (isClosed) {
                formMap[form].closed += total;
            } else {
                formMap[form].open += total;
            }

            formMap[form].total = formMap[form].open + formMap[form].closed;
        };

        checkouts.forEach(accumulate);

        const values = Object.values(formMap);
        const totals = values.reduce(
            (acc, item) => {
                acc.open += item.open;
                acc.closed += item.closed;
                acc.total += item.total;
                return acc;
            },
            { key: "total", name: "Всього", open: 0, closed: 0, total: 0 },
        );

        return [...values, totals];
    }, [checkouts]);

    const columns: ColumnsType<(typeof data)[0]> = [
        {
            title: "",
            dataIndex: "name",
            key: "name",
            render: (text) => (
                <StyledColumnNameText>{text}</StyledColumnNameText>
            ),
        },
        {
            title: <StyledColumnNameText>Відкриті</StyledColumnNameText>,
            dataIndex: "open",
            key: "open",
            align: "right",
            render: (value: number) => (
                <StyledValueText>{formatUAHNarrow(value)}</StyledValueText>
            ),
        },
        {
            title: <StyledColumnNameText>Закриті</StyledColumnNameText>,
            dataIndex: "closed",
            key: "closed",
            align: "right",
            render: (value: number) => (
                <StyledValueText>{formatUAHNarrow(value)}</StyledValueText>
            ),
        },
        {
            title: <StyledColumnNameText>Разом</StyledColumnNameText>,
            dataIndex: "total",
            key: "total",
            align: "right",
            render: (value: number) => (
                <StyledValueText>{formatUAHNarrow(value)}</StyledValueText>
            ),
        },
    ];

    const totalSum = data.find((item) => item.key === "total")?.total || 0;

    const collapseItems: CollapseProps["items"] = [
        {
            key: "1",
            label: (
                <Flex justify="space-between" align="center">
                    <PrimaryText>Загальна сума</PrimaryText>

                    <StyledTotalSumText fontWeight="500">
                        {formatUAHStandard(totalSum)}
                    </StyledTotalSumText>
                </Flex>
            ),
            children: (
                <StyledTableWrapper>
                    <Table
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        bordered
                    />
                </StyledTableWrapper>
            ),
        },
    ];

    return (
        <StyledCollapseWrapper>
            <StyledCollapse items={collapseItems} />
        </StyledCollapseWrapper>
    );
};
